import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

const ALLOWED_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
const ALLOWED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp']);
let detachedFormCounter = 0;

/**
 * A product-form dialog that submits pet details now or flags them for later.
 */
class PetPhotoUpload extends DialogComponent {
  requiredRefs = [
    'dialog',
    'error',
    'submitButton',
    'submitLabel',
    'formSubmit',
    'fileInputs',
    'fileItems',
    'previewImages',
    'fileNames',
    'selectButtons',
    'removeButtons',
    'acknowledgementDialog',
    'acknowledgementCheckbox',
    'acknowledgementSubmitButton',
    'acknowledgementError',
  ];

  /** @type {'cart' | 'checkout'} */
  #action = 'cart';

  /** @type {HTMLElement | null} */
  #opener = null;

  /** @type {AbortController | null} */
  #resultController = null;

  /** @type {Map<number, string>} */
  #previewUrls = new Map();

  #processing = false;

  #opening = false;

  /** @type {HTMLFormElement | null} */
  #formElement = null;

  /** @type {HTMLElement | null} */
  #productFormElement = null;

  #detachedFromQuickAdd = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    this.refs.acknowledgementDialog.addEventListener('click', this.#stopAcknowledgementClickPropagation);
    this.#syncPetNameFields();
    this.#syncPhotoFields();
  }

  disconnectedCallback() {
    this.#resultController?.abort();
    this.#revokeAllPreviewUrls();
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    this.refs.acknowledgementDialog.removeEventListener('click', this.#stopAcknowledgementClickPropagation);
    super.disconnectedCallback();
  }

  /** @param {Event} event */
  async openCart(event) {
    await this.#open('cart', event);
  }

  /** @param {Event} event */
  async openCheckout(event) {
    await this.#open('checkout', event);
  }

  /**
   * @param {'cart' | 'checkout'} action
   * @param {Event} event
   */
  async #open(action, event) {
    if (this.#processing || this.#opening || this.refs.dialog.open) return;

    this.#opening = true;

    this.#action = action;
    this.#opener = event.target instanceof HTMLElement ? event.target.closest('button, a') : null;
    this.refs.submitLabel.textContent =
      action === 'checkout' ? this.dataset.buyNowLabel || 'Buy it now' : this.dataset.addToCartLabel || 'Add to cart';
    this.#clearError();

    this.#formElement = this.closest('form');
    this.#productFormElement = this.closest('product-form-component');
    this.#syncPetNameFields();
    this.#syncPhotoFields();

    // Quick add contains the product form and this dialog in the same modal.
    // Move personalization to the page root before closing quick add, while
    // explicitly keeping every field associated with the original product form.
    const quickAddDialog = this.closest('quick-add-dialog');
    const quickAddNativeDialog = quickAddDialog?.querySelector(':scope > dialog');
    if (quickAddNativeDialog instanceof HTMLDialogElement && quickAddNativeDialog.open) {
      this.#opener = null;
      this.#detachFromQuickAdd(quickAddDialog);

      if (typeof quickAddDialog.closeDialog === 'function') {
        await quickAddDialog.closeDialog();
      } else {
        quickAddNativeDialog.close();
      }
    }

    if (!this.isConnected) {
      this.#opening = false;
      return;
    }

    this.showDialog();
    this.#opening = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const firstEmptyIndex = this.#fileInputs.findIndex((input) => !input.files?.[0]);
        const focusTarget = this.#selectButtons[firstEmptyIndex >= 0 ? firstEmptyIndex : 0];
        focusTarget?.focus();
      });
    });
  }

  /** @param {Event} event */
  async cancel(event) {
    event.preventDefault();
    if (this.#processing) return;
    await this.closeDialog();
  }

  /**
   * @param {number} index
   * @param {Event} event
   */
  selectPhoto(index, event) {
    event.preventDefault();
    if (this.#processing) return;
    this.#fileInputs[index]?.click();
  }

  /** @param {Event} event */
  handleFileChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const index = Number(input.dataset.photoIndex);
    const file = input.files?.[0];

    if (!file) {
      this.#clearFile(index);
      return;
    }

    if (!this.#isAllowedImage(file)) {
      input.value = '';
      this.#clearFile(index);
      this.#showError(this.dataset.invalidTypeError || 'Please upload a JPG, JPEG, PNG, or WEBP image.');
      return;
    }

    this.#clearError();
    this.#setPreview(index, file);
  }

  /** @param {Event} event */
  handlePetCountChange(event) {
    event.preventDefault();
    this.#syncPetNameFields();
    this.#syncPhotoFields();
  }

  /**
   * @param {number} index
   * @param {Event} event
   */
  removePhoto(index, event) {
    event.preventDefault();
    if (this.#processing) return;

    const input = this.#fileInputs[index];
    if (input) input.value = '';
    this.#clearFile(index);
    this.#selectButtons[index]?.focus();
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (!this.#processing) this.closeDialog();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      this.refs.dialog.querySelectorAll(
        'button:not([disabled]):not([hidden]), a[href], input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element instanceof HTMLElement && !element.hidden && element.getClientRects().length > 0);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** @param {Event} event */
  submit(event) {
    event.preventDefault();
    if (this.#processing || !this.#validate()) return;
    this.#openAcknowledgement();
  }

  /** @param {Event} event */
  skip(event) {
    event.preventDefault();
    this.#submit(true);
  }

  #openAcknowledgement() {
    const dialog = this.refs.acknowledgementDialog;
    const checkbox = this.refs.acknowledgementCheckbox;
    const submitButton = this.refs.acknowledgementSubmitButton;

    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    checkbox.checked = false;
    submitButton.disabled = true;
    this.#clearAcknowledgementError();
    dialog.showModal();

    requestAnimationFrame(() => checkbox.focus());
  }

  /** @param {Event} event */
  handleAcknowledgementChange(event) {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;

    this.refs.acknowledgementSubmitButton.disabled = !checkbox.checked;
    if (checkbox.checked) this.#clearAcknowledgementError();
  }

  /** @param {Event} event */
  confirmAcknowledgement(event) {
    event.preventDefault();
    if (this.#processing) return;

    const checkbox = this.refs.acknowledgementCheckbox;
    if (!checkbox.checked) {
      this.#showAcknowledgementError();
      checkbox.focus();
      return;
    }

    this.refs.acknowledgementDialog.close();
    this.#submit(false);
  }

  /** @param {Event} event */
  cancelAcknowledgement(event) {
    event.preventDefault();
    if (this.#processing) return;
    this.#closeAcknowledgement();
  }

  /** @param {KeyboardEvent} event */
  handleAcknowledgementKeydown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    if (!this.#processing) this.#closeAcknowledgement();
  }

  #closeAcknowledgement() {
    const dialog = this.refs.acknowledgementDialog;
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    requestAnimationFrame(() => this.refs.submitButton.focus());
  }

  #stopAcknowledgementClickPropagation = (event) => {
    event.stopPropagation();
  };

  #showAcknowledgementError() {
    const error = this.refs.acknowledgementError;
    error.textContent =
      this.dataset.acknowledgementError ||
      'Please confirm that you understand the artwork, revision, approval, and cancellation terms.';
    error.hidden = false;
  }

  #clearAcknowledgementError() {
    const error = this.refs.acknowledgementError;
    error.textContent = '';
    error.hidden = true;
  }

  /** @param {boolean} deferDetails */
  #submit(deferDetails) {
    if (this.#processing || (!deferDetails && !this.#validate())) return;

    const form = this.#form;
    const productForm = this.#productForm;
    const variantInput = form?.querySelector('input[name="id"]');
    const addToCartButton = productForm?.querySelector('[ref="addToCartButton"]');

    if (
      !(form instanceof HTMLFormElement) ||
      !productForm ||
      !(variantInput instanceof HTMLInputElement) ||
      !variantInput.value ||
      (addToCartButton instanceof HTMLButtonElement && addToCartButton.disabled)
    ) {
      this.#showError(this.dataset.unavailableError || 'Please select an available product option.');
      return;
    }

    const deferredDetailsInput = this.querySelector('[data-deferred-details]');
    if (deferredDetailsInput instanceof HTMLInputElement) deferredDetailsInput.disabled = !deferDetails;

    const detailInputs = Array.from(this.querySelectorAll('[data-personalization-input]'));
    const emptyFileInputs = this.#fileInputs.filter((input) => !input.files?.[0] && !input.disabled);
    const emptyOptionalInputs = detailInputs.filter(
      (input) =>
        (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement) &&
        !input.disabled &&
        !input.required &&
        !input.value.trim()
    );
    const temporarilyDisabledInputs = deferDetails
      ? [...new Set([...this.#fileInputs, ...detailInputs])]
      : [...new Set([...emptyFileInputs, ...emptyOptionalInputs])];
    const originalDisabledStates = new Map(temporarilyDisabledInputs.map((input) => [input, input.disabled]));
    for (const input of temporarilyDisabledInputs) input.disabled = true;

    try {
      if (!form.reportValidity()) {
        this.#showError(this.dataset.generalError || 'Please check the required information and try again.');
        if (deferredDetailsInput instanceof HTMLInputElement) deferredDetailsInput.disabled = true;
        return;
      }

      this.#clearError();
      this.#setProcessing(true);
      this.#listenForCartResult();

      form.dataset.personalizationSubmit = 'true';
      form.requestSubmit(this.refs.formSubmit);
    } catch (error) {
      console.error(error);
      this.#finishWithError(this.dataset.generalError);
    } finally {
      // ProductFormComponent builds FormData synchronously during requestSubmit.
      // Keep the deferred-details marker enabled until a queued variant change finishes.
      for (const [input, wasDisabled] of originalDisabledStates) input.disabled = wasDisabled;
      delete form.dataset.personalizationSubmit;
    }
  }

  #listenForCartResult() {
    this.#resultController?.abort();
    this.#resultController = new AbortController();
    const { signal } = this.#resultController;
    const productForm = this.#productForm;

    if (!productForm) {
      this.#finishWithError(this.dataset.generalError);
      return;
    }

    productForm.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    productForm.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
  }

  /** @param {CustomEvent} event */
  #handleCartUpdate = async (event) => {
    if (event.detail?.data?.source !== 'product-form-component') return;

    if (event.detail?.data?.didError) {
      this.#finishWithError(event.detail?.data?.message || this.dataset.generalError);
      return;
    }

    this.#resultController?.abort();

    if (this.#action === 'checkout') {
      window.location.assign(this.dataset.checkoutUrl || '/checkout');
      return;
    }

    this.#setProcessing(false);
    this.#resetPersonalization();
    await this.closeDialog();
  };

  /** @param {CustomEvent} event */
  #handleCartError = (event) => {
    this.#finishWithError(event.detail?.data?.message || event.detail?.data?.description || this.dataset.generalError);
  };

  /** @param {string | undefined} message */
  #finishWithError(message) {
    this.#resultController?.abort();
    this.#setProcessing(false);
    const deferredDetailsInput = this.querySelector('[data-deferred-details]');
    if (deferredDetailsInput instanceof HTMLInputElement) deferredDetailsInput.disabled = true;
    this.#showError(message || 'Something went wrong. Please try again.');
  }

  #validate() {
    const minimum = Number(this.dataset.minimum) || 0;
    const requiredInputs = this.#fileInputs.filter((input) => input.required && !input.disabled);
    const hasMissingRequired = requiredInputs.some((input) => !input.files?.[0]);

    if (hasMissingRequired) {
      const template = this.dataset.minimumError || 'Please upload at least [count] photos before continuing.';
      this.#showError(template.replace(/\[count\]/gi, String(minimum)));
      return false;
    }

    for (const input of this.#fileInputs) {
      const file = input.files?.[0];
      if (file && !this.#isAllowedImage(file)) {
        this.#showError(this.dataset.invalidTypeError || 'Please upload a JPG, JPEG, PNG, or WEBP image.');
        return false;
      }
    }

    const requiredPersonalizationInputs = Array.from(
      this.querySelectorAll('[data-personalization-input][required]:not(:disabled)')
    );
    const firstMissingInput = requiredPersonalizationInputs.find(
      (input) =>
        (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement) &&
        !input.value.trim()
    );

    if (
      firstMissingInput instanceof HTMLInputElement ||
      firstMissingInput instanceof HTMLTextAreaElement ||
      firstMissingInput instanceof HTMLSelectElement
    ) {
      this.#showError(this.dataset.requiredFieldsError || 'Please complete all required personalization fields.');
      firstMissingInput.focus();
      return false;
    }

    this.#clearError();
    return true;
  }

  /** @param {File} file */
  #isAllowedImage(file) {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const validExtension = ALLOWED_EXTENSIONS.has(extension);
    const validMime = ALLOWED_MIME_TYPES.has(file.type.toLowerCase());

    return validExtension && (!file.type || validMime);
  }

  /**
   * @param {number} index
   * @param {File} file
   */
  #setPreview(index, file) {
    this.#revokePreviewUrl(index);

    const previewUrl = URL.createObjectURL(file);
    const preview = this.#previewImages[index];
    const fileName = this.#fileNames[index];
    const item = this.#fileItems[index];
    const selectButton = this.#selectButtons[index];
    const removeButton = this.#removeButtons[index];

    this.#previewUrls.set(index, previewUrl);
    preview.src = previewUrl;
    preview.hidden = false;
    fileName.textContent = file.name;
    fileName.hidden = false;
    item.dataset.hasFile = 'true';
    selectButton.textContent = this.dataset.changeLabel || 'Change photo';
    removeButton.hidden = false;
  }

  /** @param {number} index */
  #clearFile(index) {
    this.#revokePreviewUrl(index);

    const preview = this.#previewImages[index];
    const fileName = this.#fileNames[index];
    const item = this.#fileItems[index];
    const selectButton = this.#selectButtons[index];
    const removeButton = this.#removeButtons[index];

    preview.removeAttribute('src');
    preview.hidden = true;
    fileName.textContent = '';
    fileName.hidden = true;
    item.dataset.hasFile = 'false';
    selectButton.textContent = this.dataset.uploadLabel || 'Upload photo';
    removeButton.hidden = true;
  }

  #resetFiles() {
    this.#fileInputs.forEach((input, index) => {
      input.value = '';
      this.#clearFile(index);
    });
    this.#clearError();
  }

  #resetPersonalization() {
    this.#resetFiles();

    const deferredDetailsInput = this.querySelector('[data-deferred-details]');
    if (deferredDetailsInput instanceof HTMLInputElement) deferredDetailsInput.disabled = true;

    for (const input of this.querySelectorAll('[data-personalization-input]')) {
      if (input instanceof HTMLSelectElement) {
        input.selectedIndex = 0;
      } else if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
        input.value = '';
      }
    }

    this.#syncPetNameFields();
    this.#syncPhotoFields();
  }

  #syncPhotoFields() {
    const hasPetCountChoice = this.dataset.hasPetCountChoice === 'true';
    const configuredMinimum = Math.max(Number(this.dataset.minimum) || 0, 0);

    if (!hasPetCountChoice) {
      this.removeAttribute('data-split-pets');

      this.#fileItems.forEach((field, index) => {
        const input = this.#fileInputs[index];
        field.hidden = false;
        if (!input) return;
        input.disabled = false;
        input.required = index < configuredMinimum;
        input.toggleAttribute('aria-required', input.required);
      });
      return;
    }

    const maximumPets = Math.max(Number(this.dataset.maximumPets) || 1, 1);
    const photosPerPet = Math.max(Number(this.dataset.photosPerPet) || 4, 1);
    const selectedPetCount = this.#getSelectedPetCount(maximumPets);
    const visiblePhotoCount = Math.min(this.#fileInputs.length, selectedPetCount * photosPerPet);
    const minimumPhotoCount = Math.min(configuredMinimum, photosPerPet);

    this.dataset.splitPets = selectedPetCount > 1 ? 'true' : 'false';

    for (const group of this.querySelectorAll('[data-pet-photo-group]')) {
      if (!(group instanceof HTMLElement)) continue;
      const petNumber = Number(group.dataset.petNumber) || 1;
      group.hidden = petNumber > selectedPetCount;
    }

    this.#fileItems.forEach((field, index) => {
      const input = this.#fileInputs[index];
      const active = index < visiblePhotoCount;

      field.hidden = !active;

      if (!input) return;

      if (!active && input.files?.[0]) {
        input.value = '';
        this.#clearFile(index);
      }

      input.disabled = !active;
      const photoPositionWithinPet = index % photosPerPet;
      input.required = active && photoPositionWithinPet < minimumPhotoCount;
      input.toggleAttribute('aria-required', input.required);
    });
  }

  #syncPetNameFields() {
    const fields = Array.from(this.querySelectorAll('[data-pet-name-field]'));
    if (!fields.length) return;

    const selectedPetCount = this.#getSelectedPetCount(fields.length);
    const singularLabel = this.dataset.petNameSingular || 'Pet name';
    const multipleLabelTemplate = this.dataset.petNameMultiple || 'Pet [count] name';

    fields.forEach((field, index) => {
      if (!(field instanceof HTMLElement)) return;

      const petNumber = index + 1;
      const active = petNumber <= selectedPetCount;
      const input = field.querySelector('[data-pet-name-input]');
      const label = field.querySelector('[data-pet-name-label]');

      field.hidden = !active;

      if (label) {
        label.textContent =
          selectedPetCount === 1
            ? singularLabel
            : multipleLabelTemplate.replace(/\[count\]/gi, String(petNumber));
      }

      if (input instanceof HTMLInputElement) {
        input.disabled = !active;
        input.required = active;
        input.toggleAttribute('aria-required', active);
        input.name =
          selectedPetCount === 1 ? 'properties[Pet name]' : `properties[Pet ${petNumber} name]`;
      }
    });
  }

  /** @param {number} maximum */
  #getSelectedPetCount(maximum) {
    const modalPetCount = this.querySelector('[data-pet-count-input]');
    if (modalPetCount instanceof HTMLSelectElement) {
      const selectedCount = Number.parseInt(modalPetCount.value, 10);
      return Math.min(Math.max(selectedCount || 1, 1), maximum);
    }

    const productForm = this.#productForm;
    const scope = productForm?.closest('.shopify-section, dialog, product-card') || productForm;
    if (!scope) return 1;

    const optionControls = Array.from(scope.querySelectorAll('input:checked, select'));
    const petCountControl = optionControls.find((control) => {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return false;

      const fieldsetLabel = control.closest('fieldset')?.querySelector(':scope > legend')?.textContent || '';
      const explicitLabel = control.id ? scope.querySelector(`label[for="${CSS.escape(control.id)}"]`)?.textContent || '' : '';
      const identifyingText = `${control.name} ${fieldsetLabel} ${explicitLabel}`;

      return /number\s+of\s+pets|pet\s+count/i.test(identifyingText);
    });

    if (!(petCountControl instanceof HTMLInputElement || petCountControl instanceof HTMLSelectElement)) return 1;

    const parsedCount = Number.parseInt(petCountControl.value.match(/\d+/)?.[0] || '1', 10);
    return Math.min(Math.max(parsedCount || 1, 1), maximum);
  }

  /** @param {number} index */
  #revokePreviewUrl(index) {
    const previewUrl = this.#previewUrls.get(index);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    this.#previewUrls.delete(index);
  }

  #revokeAllPreviewUrls() {
    for (const previewUrl of this.#previewUrls.values()) URL.revokeObjectURL(previewUrl);
    this.#previewUrls.clear();
  }

  /** @param {boolean} processing */
  #setProcessing(processing) {
    this.#processing = processing;
    this.refs.dialog.toggleAttribute('aria-busy', processing);
    this.refs.submitButton.disabled = processing;
    this.refs.submitLabel.textContent = processing
      ? this.dataset.processingLabel || 'Please wait…'
      : this.#action === 'checkout'
        ? this.dataset.buyNowLabel || 'Buy it now'
        : this.dataset.addToCartLabel || 'Add to cart';

    for (const button of this.querySelectorAll('button:not([ref="submitButton"]):not([ref="formSubmit"])')) {
      button.disabled = processing;
    }
  }

  /** @param {string} message */
  #showError(message) {
    this.refs.error.textContent = message;
    this.refs.error.hidden = false;
    this.refs.error.focus();
  }

  #clearError() {
    this.refs.error.textContent = '';
    this.refs.error.hidden = true;
  }

  /** @param {Element} quickAddDialog */
  #detachFromQuickAdd(quickAddDialog) {
    const form = this.#formElement;
    if (!(form instanceof HTMLFormElement)) return;

    const previousFormId = form.id;
    const uniqueFormId = `PetPersonalizationForm-${Date.now()}-${++detachedFormCounter}`;

    if (previousFormId) {
      const escapedPreviousFormId = CSS.escape(previousFormId);
      for (const control of quickAddDialog.querySelectorAll(`[form="${escapedPreviousFormId}"]`)) {
        control.setAttribute('form', uniqueFormId);
      }
    }

    form.id = uniqueFormId;

    for (const control of this.querySelectorAll('input, select, textarea, button')) {
      control.setAttribute('form', uniqueFormId);
    }

    this.#detachedFromQuickAdd = true;
    document.body.append(this);
  }

  #restoreFocus = () => {
    if (this.#detachedFromQuickAdd) {
      this.#detachedFromQuickAdd = false;
      requestAnimationFrame(() => this.remove());
      return;
    }

    if (this.#opener?.isConnected) this.#opener.focus();
  };

  /** @returns {HTMLInputElement[]} */
  get #fileInputs() {
    return /** @type {HTMLInputElement[]} */ (this.refs.fileInputs);
  }

  /** @returns {HTMLElement[]} */
  get #fileItems() {
    return /** @type {HTMLElement[]} */ (this.refs.fileItems);
  }

  /** @returns {HTMLImageElement[]} */
  get #previewImages() {
    return /** @type {HTMLImageElement[]} */ (this.refs.previewImages);
  }

  /** @returns {HTMLElement[]} */
  get #fileNames() {
    return /** @type {HTMLElement[]} */ (this.refs.fileNames);
  }

  /** @returns {HTMLButtonElement[]} */
  get #selectButtons() {
    return /** @type {HTMLButtonElement[]} */ (this.refs.selectButtons);
  }

  /** @returns {HTMLButtonElement[]} */
  get #removeButtons() {
    return /** @type {HTMLButtonElement[]} */ (this.refs.removeButtons);
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    return this.#formElement || this.closest('form');
  }

  /** @returns {HTMLElement | null} */
  get #productForm() {
    return this.#productFormElement || this.closest('product-form-component');
  }
}

if (!customElements.get('pet-photo-upload')) {
  customElements.define('pet-photo-upload', PetPhotoUpload);
}
